// Ingest ceilings + decompression-bomb control (szzt-spec.md §3.1, §14, §A.4 #11).
//
// The front-end accepts a folder, loose files, or a zip. For a zip, the §3.1 ceilings MUST be
// enforced against the DECOMPRESSED size — and the compression ratio bounded — BEFORE the tree is
// materialized, so a tiny archive cannot expand to exhaust ingest (decompression-bomb control). This
// module is the pure, transport-free kernel of that check: `assertArchiveWithinCeilings` validates a
// zip's central-directory metadata up front, and `RatioGuard` is the streaming backstop for an
// inflater whose declared sizes cannot be trusted. The actual zip decoding lives in the browser glue
// (`browser.ts`); this module holds only the numeric guards, so they are unit-tested in isolation.
//
// Once a tree is materialized as `FileInput[]`, `@szzt/core`'s `build`/`normalize` re-enforces the
// same §3.1 ceilings on the real bytes (plus path normalization and root inference, §14) — so this is
// the EARLY guard that protects the unpacker itself, not a replacement for the build-time check.

import { MAX_FILE_BYTES, MAX_TOTAL_BYTES, MAX_FILE_COUNT } from "@szzt/core";

// Re-export the shared §3.1 ceilings so an ingest caller reads them from one place.
export { MAX_FILE_BYTES, MAX_TOTAL_BYTES, MAX_FILE_COUNT };

/**
 * Maximum overall decompressed:compressed ratio for an archive (§A.4 #11). A well-formed archive of
 * ordinary web assets sits far below this; a decompression bomb is characterized by an extreme ratio.
 * Tunable, like the §3.1 ceilings. The ratio is only checked once decompressed output passes
 * {@link RATIO_FLOOR_BYTES}, so a tiny highly-compressible file (e.g. a few KB that inflates 200x)
 * is not a false positive.
 */
export const MAX_COMPRESSION_RATIO = 200;

/**
 * Floor below which the ratio check is skipped. Small outputs have volatile ratios (a 20-byte deflate
 * stream can inflate to KBs legitimately); the bomb signal is a large output at an extreme ratio.
 */
export const RATIO_FLOOR_BYTES = 1 * 1024 * 1024;

/** Thrown when ingest input violates a §3.1 ceiling or the decompression-bomb bounds (§14, §A.4). */
export class IngestError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "IngestError";
  }
}

/**
 * One archive entry's declared sizes, as read from a zip central directory BEFORE inflating. `path`
 * is the raw archive path (normalization/junk-stripping happens later, in `@szzt/core`).
 */
export interface ArchiveEntryMeta {
  path: string;
  /** Bytes the entry occupies compressed in the archive. */
  compressedSize: number;
  /** Bytes the entry claims it will occupy decompressed. */
  uncompressedSize: number;
}

/** Tunable bounds for the archive check; defaults are the §3.1 ceilings + {@link MAX_COMPRESSION_RATIO}. */
export interface ArchiveLimits {
  maxFileBytes?: number;
  maxTotalBytes?: number;
  maxFileCount?: number;
  maxRatio?: number;
  ratioFloorBytes?: number;
}

/**
 * Enforce the §3.1 ceilings against an archive's DECLARED decompressed sizes, plus the §A.4 ratio
 * bound, before any entry is inflated (§14). Directory entries (trailing `/`, no bytes) are ignored
 * for the count and per-file checks. Throws {@link IngestError} on the first violation.
 *
 * This is fail-fast on metadata; because a hostile archive can also lie about its declared sizes, an
 * inflater MUST additionally stream through a {@link RatioGuard} (the observed-bytes backstop).
 */
export function assertArchiveWithinCeilings(
  entries: ArchiveEntryMeta[],
  limits: ArchiveLimits = {},
): void {
  const maxFileBytes = limits.maxFileBytes ?? MAX_FILE_BYTES;
  const maxTotalBytes = limits.maxTotalBytes ?? MAX_TOTAL_BYTES;
  const maxFileCount = limits.maxFileCount ?? MAX_FILE_COUNT;
  const maxRatio = limits.maxRatio ?? MAX_COMPRESSION_RATIO;
  const ratioFloor = limits.ratioFloorBytes ?? RATIO_FLOOR_BYTES;

  const files = entries.filter((e) => !isDirectoryEntry(e));

  if (files.length > maxFileCount) {
    throw new IngestError(
      `archive holds ${files.length} files, exceeds ceiling of ${maxFileCount} (§3.1)`,
    );
  }

  let totalUncompressed = 0;
  let totalCompressed = 0;
  for (const e of files) {
    if (e.uncompressedSize < 0 || e.compressedSize < 0) {
      throw new IngestError(`archive entry ${JSON.stringify(e.path)} has a negative declared size`);
    }
    if (e.uncompressedSize > maxFileBytes) {
      throw new IngestError(
        `archive entry ${JSON.stringify(e.path)} decompresses to ${e.uncompressedSize} bytes, ` +
          `exceeds per-file ceiling ${maxFileBytes} (§3.1)`,
      );
    }
    totalUncompressed += e.uncompressedSize;
    totalCompressed += e.compressedSize;
  }

  if (totalUncompressed > maxTotalBytes) {
    throw new IngestError(
      `archive decompresses to ${totalUncompressed} bytes total, exceeds ceiling ${maxTotalBytes} (§3.1)`,
    );
  }

  // Ratio bound: only meaningful once the output is large enough to be a real bomb (§A.4 #11).
  if (totalUncompressed > ratioFloor) {
    const ratio = totalUncompressed / Math.max(1, totalCompressed);
    if (ratio > maxRatio) {
      throw new IngestError(
        `archive compression ratio ${ratio.toFixed(1)}x (${totalUncompressed} / ${totalCompressed}) ` +
          `exceeds bomb bound ${maxRatio}x (§A.4)`,
      );
    }
  }
}

/** True for a zip directory entry (path ends with `/` and carries no content). */
function isDirectoryEntry(e: ArchiveEntryMeta): boolean {
  return e.path.endsWith("/") && e.uncompressedSize === 0;
}

/**
 * Streaming backstop for an inflater whose declared sizes cannot be trusted (§14, §A.4 #11). An
 * inflate loop feeds compressed input via {@link addInput} and each produced output chunk via
 * {@link addOutput}; the guard throws {@link IngestError} the moment cumulative output crosses the
 * per-file or total ceiling, or the observed ratio exceeds the bomb bound past the floor — so a lying
 * archive is aborted mid-stream rather than after it has already exhausted memory.
 */
export class RatioGuard {
  private readonly maxFileBytes: number;
  private readonly maxTotalBytes: number;
  private readonly maxRatio: number;
  private readonly ratioFloor: number;
  private compressed = 0;
  private decompressed = 0;
  /** Output produced for the current entry; reset via {@link nextEntry}. */
  private entryOutput = 0;

  constructor(limits: ArchiveLimits = {}) {
    this.maxFileBytes = limits.maxFileBytes ?? MAX_FILE_BYTES;
    this.maxTotalBytes = limits.maxTotalBytes ?? MAX_TOTAL_BYTES;
    this.maxRatio = limits.maxRatio ?? MAX_COMPRESSION_RATIO;
    this.ratioFloor = limits.ratioFloorBytes ?? RATIO_FLOOR_BYTES;
  }

  /** Begin accounting a new archive entry (resets the per-file output counter). */
  nextEntry(): void {
    this.entryOutput = 0;
  }

  /** Account `bytes` of compressed input consumed. */
  addInput(bytes: number): void {
    this.compressed += bytes;
  }

  /** Account `bytes` of decompressed output produced; throws if any bound is crossed. */
  addOutput(bytes: number): void {
    this.entryOutput += bytes;
    this.decompressed += bytes;
    if (this.entryOutput > this.maxFileBytes) {
      throw new IngestError(
        `inflated entry exceeded per-file ceiling ${this.maxFileBytes} bytes mid-stream (§A.4)`,
      );
    }
    if (this.decompressed > this.maxTotalBytes) {
      throw new IngestError(
        `inflated output exceeded total ceiling ${this.maxTotalBytes} bytes mid-stream (§A.4)`,
      );
    }
    if (this.decompressed > this.ratioFloor) {
      const ratio = this.decompressed / Math.max(1, this.compressed);
      if (ratio > this.maxRatio) {
        throw new IngestError(
          `inflated compression ratio ${ratio.toFixed(1)}x exceeded bomb bound ${this.maxRatio}x mid-stream (§A.4)`,
        );
      }
    }
  }

  /** Total decompressed bytes observed so far. */
  get decompressedBytes(): number {
    return this.decompressed;
  }
}
